import React, { useState, useCallback } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChargingStation,
  faBatteryThreeQuarters,
  faLock,
  faLockOpen,
  faSignal,
  faLocationArrow,
  faMapMarker
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { bikeEnumToNumStatuses, bikeNumToEnumStatuses } from "data/bike-numeric-statuses";
import BikeFilers from "views/pages/bikes/filters";
import {
  openCustomModal,
  closeCustomModal
} from "views/lib/components/modals";
import {
  getBikeList,
  updateBikeStatus
} from "actions/bike/actions";

import ModalBikeLastPosition from "views/pages/bikes-history/modal-bike-last-position";
import getProgressBar from "./helpers/get-progress-bar";
import CommonTable from "../../lib/components/CommonTable";

const Bikes = ({
                  list,
                  getBikeList,
                  openCustomModal,
                  closeCustomModal,
                  updateBikeStatus
                }) => {
  const [selectedIds, setSelectedIds] = useState({});

  const toggleRow = useCallback((bikeId) => {
    let newSelectedIds = {...selectedIds};
    if (bikeId) {
      if (newSelectedIds[bikeId]) {
        delete newSelectedIds[bikeId];
      } else {
        newSelectedIds[bikeId] = true;
      }
    } else if (Object.keys(newSelectedIds).length) {
      newSelectedIds = {};
    }

    setSelectedIds(newSelectedIds);
  }, [selectedIds]);

  const handleOpenMap = useCallback((position) => {
    if (!position.coordinates || !Array.isArray(position.coordinates)) {
      toast("Coordinates not available", {
        type: toast.TYPE.ERROR,
      });
      return null;
    }
    const modalObj = {
      id: "bike-history-position",
      modalSettings: {
        size: "sm"
      },
      header: "Bike last position",
      children: (
        <ModalBikeLastPosition
          coordinates={position.coordinates}
          onCancel={() => closeCustomModal(modalObj)}
          googleMapURL="https://maps.googleapis.com/maps/api/js?key=AIzaSyCC-gpzzG6hcmAyagvaYCc23KAy4rIkv2A&v=3.exp&libraries=geometry,drawing,places,visualization"
          loadingElement={<div style={{ height: "100%" }}/>}
          containerElement={<div style={{ height: 400 }}/>}
          mapElement={<div style={{ height: "100%" }}/>}
        />
      ),
      handleClose: () => closeCustomModal(modalObj)
    };
    openCustomModal(modalObj);
  }, [openCustomModal, closeCustomModal]);

  const handleChangeStatus = useCallback((bikeId, newStatus) => {
    updateBikeStatus(bikeId, bikeNumToEnumStatuses[newStatus]);
  }, [updateBikeStatus]);

  const getColumns = () => [
    {
      id: "checkbox",
      accessor: "",
      // eslint-disable-next-line react/display-name
      Cell: ({ original }) => (
        <input
          type="checkbox"
          className="checkbox"
          checked={!!selectedIds[original.id]}
          onChange={() => toggleRow(original.id)}
        />
      ),
      Header: "",
      sortable: false,
      filterable: false,
      resizable: false,
      minResizeWidth: 50,
      width: 50,
    },
    {
      id: "ID",
      Header: "#",
      accessor: "id",
      // eslint-disable-next-line react/display-name
      Cell: ({ original }) => (
        <Link to={`/bikes/${original.id}`}>
          {original.id}
        </Link>
      )
    },
    {
      id: "qr",
      Header: "QR",
      accessor: "qr",
    },
    {
      id: "tags",
      Header: "Tags",
      Cell: ({ original }) => (original.tags
        ? original.tags.map(item => <span className="badge badge-warning" key={item}>{item}</span>)
        : ""),
      style: {
        whiteSpace: "unset"
      }
    },
    {
      id: "speed",
      Header: "Speed",
      // eslint-disable-next-line react/display-name
      accessor: original => (
        <div className={original.condition.speed ? "text-success" : "text-muted"}>
          {original.condition.speed} km/h
        </div>
      ),
    },
    {
      id: "lastRideUser",
      Header: "Last Rider",
      accessor: "lastRideUser",
      // eslint-disable-next-line react/display-name
      Cell: ({ original }) => {
        if (original.lastRideUser) {
          return (
            <Link to={`/users/${original.lastRideUser}`}>
              {original.lastRideUser}
            </Link>
          );
        }
        return null;
      }
    },
    {
      id: "lastBookUser",
      Header: "Last Booked",
      accessor: "lastBookUser",
      // eslint-disable-next-line react/display-name
      Cell: ({ original }) => {
        if (original.lastBookUser) {
          return (
            <Link to={`/users/${original.lastBookUser}`}>
              {original.lastBookUser}
            </Link>
          );
        }
        return null;
      }
    },
    {
      id: "voltage",
      Header: "Voltage",
      className: "text-center",
      headerClassName: "text-center",
      accessor: original => original.condition.voltage,
      // eslint-disable-next-line react/display-name
      Cell: ({ original }) => (
        <div>
          {getProgressBar(original.condition)}
        </div>
      )
    },
    {
      id: "charging",
      Header: "Charging",
      className: "text-center",
      headerClassName: "text-center",
      accessor: original => original.condition.charging,
      // eslint-disable-next-line react/display-name
      Cell: ({ original }) => (
        <div>
          <FontAwesomeIcon
            icon={original.condition.charging
              ? faChargingStation
              : faBatteryThreeQuarters}
            className={original.condition.charging ? "text-danger" : "text-success"}
          />
        </div>
      )
    },
    {
      id: "gps",
      Header: "GPS",
      className: "text-center",
      headerClassName: "text-center",
      accessor: original => original.condition.gps,
      // eslint-disable-next-line react/display-name
      Cell: ({ original }) => (
        <div>
          <FontAwesomeIcon
            icon={faLocationArrow}
            className={original.condition.gps ? "text-success" : "text-danger"}
          />
        </div>
      ),
    },
    {
      id: "online",
      Header: "Online",
      className: "text-center",
      headerClassName: "text-center",
      accessor: original => original.connected.online,
      // eslint-disable-next-line react/display-name
      Cell: ({ original }) => (
        <div>
          <FontAwesomeIcon
            icon={faSignal}
            className={original.connected.online ? "text-success" : "text-danger"}
          />
        </div>
      )
    },
    {
      id: "sim",
      Header: "Sim",
      accessor: original => original.sim,
      // eslint-disable-next-line react/display-name
      Cell: ({ original }) => (
        <div>
          <div>{original.sim && original.sim.phone ? original.sim.phone : "No Phone"}</div>
          <small
            className="text-muted">{original.sim && original.sim.serial ? original.sim.serial : "No Serial"}</small>
        </div>
      ),
    },
    {
      id: "locked",
      Header: "Locked",
      className: "text-center",
      headerClassName: "text-center",
      accessor: original => original.condition.closed,
      // eslint-disable-next-line react/display-name
      Cell: ({ original }) => (
        <div>
          <FontAwesomeIcon
            icon={original.condition.closed
              ? faLock
              : faLockOpen}
            className={original.condition.closed ? "text-success" : "text-danger"}
          />
        </div>
      )
    },
    {
      id: "on-map",
      Header: "Map",
      className: "text-center",
      headerClassName: "text-center",
      accessor: original => original.position,
      // eslint-disable-next-line react/display-name
      Cell: ({ original }) => (
        <button
          className="btn btn-sm btn-primary"
          onClick={() => handleOpenMap(original.position)}
          title="Bike last position"
        >
          <FontAwesomeIcon icon={faMapMarker}/>
        </button>
      )
    },
    {
      id: "actions",
      Header: "Actions",
      className: "text-center",
      headerClassName: "text-center",
      // eslint-disable-next-line react/display-name
      Cell: ({ original }) => (
        <button
          className={`btn btn-sm ${!original.status ? "btn-outline-success" : "btn-outline-info"}`}
          onClick={() => handleChangeStatus(original.id, original.status ? 0 : 1)}
        >
          {original.status === bikeEnumToNumStatuses.HIDDEN ? "Show" : "Hide"}
        </button>
      )
    },
  ];

  return (
    <CommonTable
      columns={getColumns()}
      list={list}
      getListFunction={getBikeList}
      FiltersComponent={BikeFilers}
      searchHookProps={{
        size: 20,
        sort: "id,asc"
      }}
    />
  );
};

const mapStateToProps = state => ({
  list: state.bike.list,
});

const mapStateToDispatch = dispatch => ({
  openCustomModal: bindActionCreators(openCustomModal, dispatch),
  closeCustomModal: bindActionCreators(closeCustomModal, dispatch),

  getBikeList: bindActionCreators(getBikeList, dispatch),
  updateBikeStatus: bindActionCreators(updateBikeStatus, dispatch),
});

export default connect(
  mapStateToProps,
  mapStateToDispatch
)(Bikes);



// WEBPACK FOOTER //
// ./src/views/pages/bikes/index.js